/**
 * Public audio facade (§12). Owns the context, the mixer, the 48 synthesized cues and the
 * three-layer procedural score. Presentation-only: reads `World` and the event bus,
 * writes nothing back, and draws randomness from `w.rng.audio` alone (spine §7, §12.3).
 */

import type { World } from '../sim/world';
import type { EventPayloads } from '../core/events';
import {
  budgetForMinute,
  currentEnvelope,
  currentIntensity,
  currentWave,
  currentWaveSegment,
  isBossWave,
  waveSegmentRemainingS,
} from '../sim/director';
import { AudioHost, type FamilyName } from './context';
import { MusicDirector } from './music';
import { SfxPlayer, type PlayOptions } from './sfx';
import { layerGains } from './mixer';
import {
  BOSS_MARK_GAIN,
  BOSS_MARK_PITCH,
  WAVE_MARK_CUE,
  WaveArc,
  musicLevel,
  planMuster,
  segmentRampS,
  waveMixTarget,
  wavePressure,
  type WaveSegment,
} from './waves';

export { AudioHost, FAMILY_NAMES } from './context';
export type { FamilyName } from './context';
export { MusicDirector, generateTheme, nextThemeSeed, scaleNote } from './music';
export type { Theme, NoteEvent } from './music';
export { SfxPlayer, CUES, RECIPES, missingRecipes, parsePitchVariation, LOOP_CUES } from './sfx';
export type { CueSpec } from './sfx';
export {
  WaveArc,
  SEGMENT_MIX,
  SEGMENT_RAMP_S,
  MIN_PRESSURE,
  MUSTER_MAX_LEAD_S,
  PRESSURE_DIVISOR,
  SEGMENT_LEVEL,
  musicLevel,
  planMuster,
  musterPulseOffsets,
  segmentRampS,
  waveMixTarget,
  wavePressure,
} from './waves';
export type { MusterPlan } from './waves';
export {
  VoiceAllocator,
  Ducker,
  layerGains,
  PRIORITY,
  FAMILY_SUB_CAP,
  VOICE_CAP,
  SFX_VOICE_CAP,
  MUSIC_RESERVED,
  ATTACK_LOCK_S,
  STEAL_FADE_S,
  dbToLinear,
  linearToDb,
} from './mixer';

/** §12.7 weapon → fire cue. Evolutions inherit their base weapon's identity. */
const FIRE_CUE: Readonly<Record<string, string>> = {
  'weapon.spear-jelly-lance': 'audio.weapons.lance-fire',
  'weapon.krill-scatter': 'audio.weapons.krill-volley',
  'weapon.barreleye-gaze': 'audio.weapons.beam-tick',
  'weapon.copepod-halo': 'audio.weapons.orbiter-tick',
  'weapon.atolla-nova': 'audio.weapons.nova-blast',
  'weapon.angler-lantern-mine': 'audio.weapons.mine-deploy',
  'weapon.siphonophore-chain-whip': 'audio.weapons.chain-zap',
  'weapon.bobtail-scion': 'audio.weapons.scion-spawn',
  'weapon.brine-pool': 'audio.weapons.pool-cast',
  'weapon.vampire-shroud': 'audio.weapons.shroud-block',
  'weapon.gulper-maw': 'audio.weapons.maw-lunge',
  'weapon.loosejaw-searchlight': 'audio.weapons.sweep-tick',
};

/** §12.7 weapon → impact cue. Anything unmapped falls back to the generic enemy hit. */
const HIT_CUE: Readonly<Record<string, string>> = {
  'weapon.spear-jelly-lance': 'audio.weapons.lance-hit',
  'weapon.krill-scatter': 'audio.weapons.krill-hit',
  'weapon.barreleye-gaze': 'audio.weapons.beam-tick',
  'weapon.copepod-halo': 'audio.weapons.orbiter-tick',
  'weapon.angler-lantern-mine': 'audio.weapons.mine-blast',
  'weapon.siphonophore-chain-whip': 'audio.weapons.chain-zap',
  'weapon.bobtail-scion': 'audio.weapons.scion-dive',
  'weapon.gulper-maw': 'audio.weapons.maw-devour',
  'weapon.loosejaw-searchlight': 'audio.weapons.sweep-tick',
  'weapon.vampire-shroud': 'audio.weapons.shroud-break',
};

const EVO_TO_WEAPON: Readonly<Record<string, string>> = {
  'evolution.kingspear-medusa': 'weapon.spear-jelly-lance',
  'evolution.krillstorm-battery': 'weapon.krill-scatter',
  'evolution.barreleye-oversight': 'weapon.barreleye-gaze',
  'evolution.copepod-corona': 'weapon.copepod-halo',
  'evolution.atolla-alarm-crown': 'weapon.atolla-nova',
  'evolution.angler-grand-lure': 'weapon.angler-lantern-mine',
  'evolution.siphonophore-aggregate': 'weapon.siphonophore-chain-whip',
  'evolution.bobtail-armada': 'weapon.bobtail-scion',
  'evolution.brine-lake-basin': 'weapon.brine-pool',
  'evolution.vampire-abyss-cloak': 'weapon.vampire-shroud',
  'evolution.gulper-worldmaw': 'weapon.gulper-maw',
  'evolution.loosejaw-eclipse-array': 'weapon.loosejaw-searchlight',
};

/** Weapons that own a continuous loop voice while equipped (§12.4). */
const WEAPON_LOOPS: Readonly<Record<string, string>> = {
  'weapon.barreleye-gaze': 'audio.weapons.beam-loop',
  'weapon.copepod-halo': 'audio.weapons.orbiter-hum',
  'weapon.brine-pool': 'audio.weapons.pool-sizzle',
  'weapon.loosejaw-searchlight': 'audio.weapons.sweep-hum',
};

const LOOP_WEAPON_IDS: readonly string[] = Object.keys(WEAPON_LOOPS);

/** §12.7 boss identity by pitch: tyrant −3%, warden 0%, medusa +3%, sovereign −6%. */
const BOSS_PITCH: Readonly<Record<string, number>> = {
  'boss.abyssal-tyrant': 0.97,
  'boss.trench-warden': 1.0,
  'boss.medusa-prime': 1.03,
  'boss.abyss-sovereign': 0.94,
};

const LOW_HP_ENTER = 0.25;
const LOW_HP_REARM = 0.3;
const POLL_MS = 100;
/** AMD-37 — intensity drift inside a beat worth re-automating for. */
const PRESSURE_DRIFT = 0.02;
/** §12.5 / §8.7 — a boss encounter forces I ≥ 0.8. */
const BOSS_INTENSITY_FLOOR = 0.8;
/** §12.6 silence rule 2 — envelope ≤ 0.70 ducks the weapons bus −3 dB. */
const RECOVERY_DUCK_INTENSITY = 0.7;
/** Hold on that duck. Short, because the 10 Hz poll re-arms it while the quiet lasts. */
const LULL_DUCK_HOLD_S = 0.25;

/** §12.5 boss floor scope. Read-only scan of the enemy pool; runs at 10 Hz. */
function bossAlive(w: World): boolean {
  const list = w.enemies.liveIndices();
  for (let i = 0; i < list.length; i++) {
    if (w.enemies.slots[list[i]].is_boss) return true;
  }
  return false;
}
const AMBIENT_CUE = 'audio.ambient.trench';

function baseWeaponId(id: string): string {
  const mapped = EVO_TO_WEAPON[id];
  return mapped ?? id;
}

export class AudioEngine {
  private readonly host = new AudioHost();
  private readonly music = new MusicDirector();
  private readonly sfx = new SfxPlayer();
  private world: World | null = null;
  private unsubs: (() => void)[] = [];
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private lowHpArmed = true;
  private surgeTier = 1;
  private started = false;
  /** AMD-37 — the wave arc's per-wave latch and its cached §12.5 phase pressure. */
  private readonly arc = new WaveArc();
  private readonly scratchGains = new Float32Array(3);
  private readonly onPoll = (): void => {
    this.poll();
  };

  /** Idempotent, safe to call before any gesture (§13 autoplay policy). */
  async init(): Promise<void> {
    await this.host.init();
    const g = this.host.graph;
    if (!g) return;
    this.music.attach(g);
    this.sfx.attach(g);
    if (!this.started) {
      this.started = true;
      this.music.start();
      this.sfx.startLoop(AMBIENT_CUE);
    }
    if (this.pollTimer === null && typeof setInterval === 'function') {
      this.pollTimer = setInterval(this.onPoll, POLL_MS);
    }
  }

  /** iOS Safari resumes only inside the gesture handler; the host does that synchronously. */
  installGestureUnlock(): void {
    this.host.installGestureUnlock();
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    const once = (): void => {
      window.removeEventListener('pointerdown', once);
      window.removeEventListener('touchstart', once);
      window.removeEventListener('keydown', once);
      void this.init();
    };
    window.addEventListener('pointerdown', once, { passive: true });
    window.addEventListener('touchstart', once, { passive: true });
    window.addEventListener('keydown', once);
  }

  /** Subscribes to the event bus. Re-binding replaces the previous subscriptions. */
  bindWorld(w: World): void {
    this.unbind();
    this.world = w;
    this.sfx.rng = w.rng.audio;
    this.music.setSeed(w.runSeed);
    const bus = w.bus;
    const u = this.unsubs;

    u.push(
      bus.on('spawn', (p: EventPayloads['spawn']) => {
        if (p.kind !== 'projectile') return;
        const cue = FIRE_CUE[baseWeaponId(p.def_id)];
        if (cue) this.sfx.play(cue);
      }),
    );

    u.push(
      bus.on('hit', (p: EventPayloads['hit']) => {
        const weapon = baseWeaponId(p.weapon_id);
        if (p.crit) this.sfx.play('audio.crit.strike');
        const cue = HIT_CUE[weapon];
        if (cue) {
          // §6.3.7 — each chain link drops 3%, cumulative.
          if (cue === 'audio.weapons.chain-zap' && p.depth > 0) {
            CHAIN_OPTS.pitch = Math.pow(0.97, p.depth);
            this.sfx.play(cue, CHAIN_OPTS);
          } else {
            this.sfx.play(cue);
          }
        } else if (!p.crit) {
          this.sfx.play('audio.enemy.hit');
        }
      }),
    );

    u.push(
      bus.on('death', (p: EventPayloads['death']) => {
        this.sfx.play(p.elite ? 'audio.enemy.death-elite' : 'audio.enemy.death-standard');
      }),
    );

    u.push(
      bus.on('player_hit', (p: EventPayloads['player_hit']) => {
        this.sfx.play('audio.player.hit');
        const player = this.world ? this.world.player : null;
        if (player && player.max_hp > 0 && p.hp_after / player.max_hp < LOW_HP_ENTER && this.lowHpArmed) {
          this.lowHpArmed = false;
          this.sfx.play('audio.warning.low-hp');
        }
      }),
    );

    u.push(
      bus.on('pickup', (p: EventPayloads['pickup']) => {
        if (p.kind === 'xp_mote') {
          // §12.3 — brightness rises +1% per Lumensurge tier, capped at +10%.
          PICKUP_OPTS.pitch = 1 + Math.min(0.1, (this.surgeTier - 1) * 0.01);
          this.sfx.play('audio.pickup.xp', PICKUP_OPTS);
        } else if (p.kind === 'health') {
          this.sfx.play('audio.pickup.health');
        } else {
          this.sfx.play('audio.pickup.powerup');
        }
      }),
    );

    u.push(bus.on('player_heal', () => this.checkLowHpRearm()));
    u.push(bus.on('level_up', () => this.sfx.play('audio.levelup.fanfare')));
    u.push(bus.on('levelup_offer', () => this.sfx.play('audio.ui.move')));
    u.push(bus.on('levelup_pick', () => this.sfx.play('audio.levelup.select')));
    u.push(
      bus.on('evolution', () => {
        // "evolution selections layer R-GONG at 0.4 gain" (audio.levelup.select acceptance).
        EVO_OPTS.layerRecipe = 'R-GONG';
        EVO_OPTS.layerGain = 0.4;
        this.sfx.play('audio.levelup.select', EVO_OPTS);
      }),
    );

    u.push(
      bus.on('dash', (p: EventPayloads['dash']) => {
        if (p.phase !== 'start') return;
        // §6.3.11 — the maw lunge replaces the dash whoosh while gulper-maw is equipped.
        this.sfx.play(this.hasWeapon('weapon.gulper-maw') ? 'audio.weapons.maw-lunge' : 'audio.player.dash');
      }),
    );

    u.push(
      bus.on('surge_change', (p: EventPayloads['surge_change']) => {
        this.surgeTier = p.to;
      }),
    );

    u.push(
      bus.on('phase_change', () => {
        // AMD-4 — crossfade the intensity layers over 2 s, sample-accurate. AMD-37: the
        // phase sets the register, the wave arc keeps modulating inside it. A phase
        // boundary is always a wave boundary (30 s waves tile a minute), so the 2 s
        // crossfade and the onset swell agree on where they are going.
        this.music.crossfadeNow(2);
      }),
    );

    u.push(
      bus.on('wave_change', (p: EventPayloads['wave_change']) => {
        // AMD-37 — punctuation, so the rhythm is countable with eyes on the arena.
        this.arc.wave = p.wave;
        MARK_OPTS.pitch = p.boss_wave ? BOSS_MARK_PITCH : 1;
        MARK_OPTS.gain = p.boss_wave ? BOSS_MARK_GAIN : 1;
        this.sfx.play(WAVE_MARK_CUE, MARK_OPTS);
      }),
    );

    u.push(
      bus.on('wave_phase', (p: EventPayloads['wave_phase']) => {
        this.arc.wave = p.wave;
        this.applyWaveSegment(p.segment, p.remaining_s);
      }),
    );

    u.push(
      bus.on('boss_spawn', (p: EventPayloads['boss_spawn']) => {
        this.sfx.play('audio.warning.boss-spawn');
        BOSS_OPTS.pitch = BOSS_PITCH[p.boss_id] ?? 1;
        this.sfx.play('audio.boss.intro', BOSS_OPTS);
        // §8.7 / §12.5 — boss encounters force I ≥ 0.8.
        this.music.setIntensityFloor(0.8);
      }),
    );

    u.push(
      bus.on('boss_phase', (p: EventPayloads['boss_phase']) => {
        BOSS_OPTS.pitch = BOSS_PITCH[p.boss_id] ?? 1;
        this.sfx.play('audio.boss.phase', BOSS_OPTS);
      }),
    );

    u.push(
      bus.on('boss_defeated', () => {
        this.music.setIntensityFloor(0);
        this.sfx.play('audio.enemy.death-elite');
      }),
    );

    u.push(
      bus.on('hazard', (p: EventPayloads['hazard']) => {
        if (p.kind === 'telegraph') this.sfx.play('audio.warning.hazard');
      }),
    );

    u.push(
      bus.on('game_over', (p: EventPayloads['game_over']) => {
        this.sfx.play(p.outcome === 'victory' ? 'audio.ui.confirm' : 'audio.ui.back');
        this.music.setIntensityFloor(0);
        this.music.setIntensity(0);
      }),
    );
  }

  private unbind(): void {
    for (let i = 0; i < this.unsubs.length; i++) this.unsubs[i]();
    this.unsubs.length = 0;
    this.world = null;
    this.arc.reset();
  }

  /**
   * AMD-37 — the wave arc. Drives the §12.5 three-layer mix from the AMD-34 beat and
   * schedules the `muster` telegraph onto the AudioContext timeline.
   *
   * Composition with §12's phase rules: the phase owns the register (its §8.2 budget is
   * `pressure`, and `phase_change` still gets AMD-4's 2 s crossfade), the wave owns the
   * breathing inside it, and the §12.5 boss floor outranks both — inside a boss fight
   * `MusicDirector` clamps I ≥ 0.8 and the lull never opens up.
   *
   * What drops out matters more than how loud it is. Falling to I ≈ 0.28 through the
   * lull takes L3 (lead + kick + clap + hats) down with it and lifts L1 (pad + drone)
   * to 0.72, and drops the R-DRONE / arp cutoffs from ~2.4 kHz to ~960 Hz. Percussion
   * and lead leave, the drone stays and darkens. That is the lull.
   */
  private applyWaveSegment(segment: WaveSegment, remainingS: number): void {
    if (this.arc.segment !== segment) this.arc.segment = segment;
    const ramp = segmentRampS(segment, remainingS);
    this.music.setIntensityOver(waveMixTarget(segment, this.arc.pressure), ramp);
    this.music.setLevel(musicLevel(segment, this.music.intensityFloor > 0), ramp);
    this.sfx.setIntensity(this.music.currentIntensity);
    if (segment === 'muster') this.scheduleMuster(remainingS);
  }

  /**
   * The telegraph. `remaining_s` counts down to the wave boundary, so the cue is placed
   * once at `ctx.currentTime + remaining_s − lead` and its swell peaks on the boundary —
   * sample-accurate on the audio clock, not chased by the 10 Hz poll.
   *
   * A boss muster is a different cue: an octave and a fifth down, three pulses instead of
   * two, an R-BOSS-ROAR growl underneath, and routed through the `boss` bus so it ducks
   * weapons −3 dB. After one boss you know which one you are hearing.
   */
  private scheduleMuster(remainingS: number): void {
    const g = this.host.graph;
    if (!g) return;
    const wave = this.arc.wave;
    if (!this.arc.armMuster(wave)) return;
    const plan = planMuster(remainingS, isBossWave(wave + 1));
    MUSTER_OPTS.durS = plan.durS;
    this.sfx.playAt(plan.cueId, g.ctx.currentTime + plan.startInS, MUSTER_OPTS);
  }

  private hasWeapon(id: string): boolean {
    const w = this.world;
    if (!w) return false;
    const slots = w.player.weapons;
    for (let i = 0; i < w.player.weapon_count && i < slots.length; i++) {
      if (baseWeaponId(slots[i].def_id) === id) return true;
    }
    return false;
  }

  private checkLowHpRearm(): void {
    const w = this.world;
    if (!w) return;
    if (w.player.max_hp > 0 && w.player.hp / w.player.max_hp > LOW_HP_REARM) this.lowHpArmed = true;
  }

  /**
   * 10 Hz housekeeping: loop start/stop within the §12.4 100 ms acceptance window,
   * pause state, self-ducking, and finished-voice collection.
   */
  private poll(): void {
    const g = this.host.graph;
    if (!g) return;
    this.sfx.collect();
    this.sfx.updateSelfDuck();
    const w = this.world;
    if (!w) return;
    this.sfx.tick = w.tick;
    this.host.setPaused(w.paused);
    this.checkLowHpRearm();
    const slots = w.player.weapons;
    for (let k = 0; k < LOOP_WEAPON_IDS.length; k++) {
      const weaponId = LOOP_WEAPON_IDS[k];
      const cue = WEAPON_LOOPS[weaponId];
      let owned = false;
      for (let i = 0; i < w.player.weapon_count && i < slots.length; i++) {
        if (baseWeaponId(slots[i].def_id) === weaponId) {
          owned = true;
          break;
        }
      }
      if (owned && !this.sfx.isLooping(cue)) this.sfx.startLoop(cue);
      else if (!owned && this.sfx.isLooping(cue)) this.sfx.stopLoop(cue);
    }
    if (w.outcome !== 'running' && this.sfx.isLooping(AMBIENT_CUE)) this.sfx.stopLoop(AMBIENT_CUE);
    if (w.outcome === 'running' && !w.paused) this.pollWaveArc(w);
  }

  /**
   * AMD-37 — the arc's steady-state driver. `wave_phase` is exact but only fires on the
   * four beat boundaries, so an engine bound mid-wave (a snapshot restore, or the debug
   * seek, which detaches presentation and rebuilds it from the world) would sit on a
   * stale mix for up to 18 s. Re-deriving the beat from the sim every 100 ms fixes that
   * and costs four pure function calls. The per-wave latch keeps the telegraph single.
   *
   * Reads only; the sim is never written and no gameplay RNG stream is touched.
   */
  private pollWaveArc(w: World): void {
    const minute = Math.floor(w.tick / 3600);
    this.arc.pressure = wavePressure(budgetForMinute(w, minute), currentEnvelope(w));
    // §12.5's I ≥ 0.8 floor is scoped to a live boss, not latched until `boss_defeated`.
    // A boss that outlives the run (or a bind that lands mid-fight) would otherwise pin
    // the arc flat for the rest of the run — measured, waves 12 and 13 froze at I = 0.8.
    const boss = bossAlive(w);
    if (boss && this.music.intensityFloor < BOSS_INTENSITY_FLOOR) this.music.setIntensityFloor(BOSS_INTENSITY_FLOOR);
    else if (!boss && this.music.intensityFloor > 0) this.music.setIntensityFloor(0);
    // §12.6 silence rule 2, driven off the AMD-34-composed intensity that AMD-34 itself
    // nominates for audio. `0.5 × envelope` puts every lull under the 0.70 threshold, so
    // the weapons bus ducks −3 dB through the quiet — the same rule §8.9 recovery windows
    // already earn. Re-armed at 10 Hz with a short hold, so it clears into the onset.
    this.sfx.setRecoveryDuck(currentIntensity(w) <= RECOVERY_DUCK_INTENSITY, LULL_DUCK_HOLD_S);
    const wave = currentWave(w);
    if (wave !== this.arc.wave) this.arc.wave = wave;
    const segment = currentWaveSegment(w);
    const target = waveMixTarget(segment, this.arc.pressure);
    if (segment !== this.arc.segment) {
      this.applyWaveSegment(segment, waveSegmentRemainingS(w));
      return;
    }
    const level = musicLevel(segment, this.music.intensityFloor > 0);
    if (Math.abs(level - this.music.currentLevel) > PRESSURE_DRIFT) this.music.setLevel(level, 1.0);
    // Pressure drifts inside a beat as the §8.2 curve and the §8.9 envelope move. Follow
    // it slowly, and only once the drift is worth an automation event.
    if (Math.abs(target - this.music.currentIntensity) > PRESSURE_DRIFT) {
      this.music.setIntensityOver(target, 1.0);
      this.sfx.setIntensity(this.music.currentIntensity);
    }
  }

  /** 0..1 director intensity — drives the 3-layer crossfade (§12.5, AMD-4). */
  setIntensity(v: number): void {
    this.music.setIntensity(v);
    this.sfx.setIntensity(this.music.currentIntensity);
  }

  /** Current layer gains, for the §12.5 ±0.05 acceptance test. */
  layerGainsNow(): Float32Array {
    return layerGains(this.music.currentIntensity, this.scratchGains);
  }

  /** §12.5 sliders, 0..1 or 0..100. Audible within 50 ms. */
  setVolumes(master: number, music: number, sfx: number): void {
    this.host.setVolumes(master, music, sfx);
  }

  setMuted(m: boolean): void {
    this.host.setMuted(m);
  }

  play(cueId: string, opts?: { pitch?: number; gain?: number }): void {
    this.sfx.play(cueId, opts as PlayOptions | undefined);
  }

  /** §8.9 recovery envelope ≤ 0.70 → weapons bus −3 dB (§12.6 silence rule 2). */
  setRecoveryDuck(active: boolean): void {
    this.sfx.setRecoveryDuck(active);
  }

  busGain(family: FamilyName): number {
    const g = this.host.graph;
    return g ? g.families[family].gain.value : 0;
  }

  suspend(): void {
    this.host.suspend();
  }

  resume(): void {
    this.host.resume();
  }

  dispose(): void {
    this.unbind();
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.music.dispose();
    this.sfx.detach();
    this.host.dispose();
    this.started = false;
  }
}

// Preallocated option objects — the event handlers run on every hit and must not allocate.
const CHAIN_OPTS: PlayOptions = { pitch: 1 };
const PICKUP_OPTS: PlayOptions = { pitch: 1 };
const BOSS_OPTS: PlayOptions = { pitch: 1 };
const EVO_OPTS: PlayOptions = { layerRecipe: '', layerGain: 0 };
const MARK_OPTS: PlayOptions = { pitch: 1, gain: 1 };
const MUSTER_OPTS: PlayOptions = { durS: 0 };
